// src/context/AuthContext.js
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api_users from "../services/api"; // aggregator: api_users.users, api_users.recipes, ...
import { persistSessionTokens, clearAuthToken } from "../services/api";
import { clearLastShoppingEntry } from "../utils/shoppingEntry";

export const AuthContext = createContext();

const onboardingDoneKey = (userId) => `onboarding_done:${userId}`;
const onboardingForceOpenKey = (userId) => `onboarding_force_open:${userId}`;

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    role: null,
    loading: true,
    krogerConnected: false,
    krogerLocationId: null,
    entitlements: null,
  });

  // Ενιαίο hydrate από /current_user (usersAPI.me) ώστε να πέρνουμε και τα Kroger flags
  const fetchEntitlementsSafe = useCallback(async () => {
    try {
      const entRes = await api_users.users.entitlements();
      return entRes?.data?.entitlements || null;
    } catch (e) {
      return null;
    }
  }, []);

  const hydrateFromCurrentUser = useCallback(async () => {
    let meStatus = "unknown";
    let entStatus = "unknown";
    try {
      const res = await api_users.users.me();
      meStatus = String(res?.status || 200);
      const { user, role, krogerConnected, krogerLocationId } = res.data || {};
      const entitlements = await fetchEntitlementsSafe();
      entStatus = entitlements ? "ok" : "none";
      setAuthState({
        user: user ? { ...user, entitlements } : null,
        role: role || null,
        loading: false,
        krogerConnected: !!krogerConnected,
        krogerLocationId: krogerLocationId ?? null,
        entitlements,
      });
      return { ok: true, data: res.data, user: user || null };
    } catch (err) {
      meStatus = String(err?.response?.status || "error");
      const entitlements = await fetchEntitlementsSafe();
      entStatus = entitlements ? "ok" : "none";
      setAuthState({
        user: null,
        role: null,
        loading: false,
        krogerConnected: false,
        krogerLocationId: null,
        entitlements,
      });
      return { ok: false, error: err, user: null };
    } finally {
      if (process.env.REACT_APP_DEBUG_AUTH === "1") {
        console.info("[Auth] hydrate", { meStatus, entStatus, loading: false });
      }
      setAuthState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
    }
  }, [fetchEntitlementsSafe]);

  // Φόρτωση τρέχοντος χρήστη στην εκκίνηση
  useEffect(() => {
    hydrateFromCurrentUser();
  }, [hydrateFromCurrentUser]);

  // --- Merge προσωρινού guest state στο account μετά από επιτυχημένο login
  const claimGuestState = async () => {
    const guest_id = localStorage.getItem("incooking_guest_id");
    if (!guest_id) return;
    try {
      const local_state = {
        // Αν κρατάς offline δεδομένα, πέρασέ τα εδώ.
        // shopping_list: JSON.parse(localStorage.getItem("guest_shopping_list") || "[]"),
        // inventory: JSON.parse(localStorage.getItem("guest_inventory") || "[]"),
      };
      await api_users.users.claimGuest(guest_id, local_state);
      // Προαιρετικά καθάρισε local προσωρινά:
      // localStorage.removeItem("guest_shopping_list");
      // localStorage.removeItem("guest_inventory");
    } catch (e) {
      console.warn("[Auth] guest/claim failed:", e?.response?.data || e.message);
    }
  };

  // ---- Logins: κρατάμε το παλιό return shape για να δουλεύει το redirect,
  //      αλλά κάνουμε hydrate & claim για να φορτώσουν flags και να γίνει merge.
  const login = async (email, password) => {
    try {
      const response = await api_users.users.login(email, password);
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      await claimGuestState();
      return { success: true, data: response.data }; // ΜΗΝ το αλλάξεις — το θέλει η φόρμα
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const loginWithGoogle = async (googleToken, { desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.googleLogin({ token: googleToken, desired_plan });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      await claimGuestState();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const loginWithApple = async ({ idToken, user = null, email = null, isPrivateEmail = false, desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.appleLogin({
        id_token: idToken,
        desired_plan,
        user,
        email,
        is_private_email: isPrivateEmail,
      });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      await claimGuestState();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const loginWithFacebook = async (facebookToken, { desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.facebookLogin({ token: facebookToken, desired_plan });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      await claimGuestState();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const loginWithFacebookCode = async (code, redirect_uri, state, { desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.facebookCodeLogin({ code, redirect_uri, state, desired_plan });
      const tokenStored = persistSessionTokens(response?.data);
      if (process.env.NODE_ENV !== "production") {
        console.debug("[Auth] facebookCodeLogin response", {
          status: response?.status,
          hasToken: !!response?.data?.auth_token,
          tokenStored,
        });
      }
      await hydrateFromCurrentUser();
      await claimGuestState();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const register = async (username, email, password, language, desired_plan = "free") => {
    try {
      const response = await api_users.users.register(username, email, password, language, desired_plan);
      // μετά το register συνήθως πας σε login, οπότε δεν κάνουμε hydrate εδώ
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const registerWithGoogle = async (googleToken, { desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.googleLogin({ token: googleToken, desired_plan });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const registerWithApple = async ({ idToken, user = null, email = null, isPrivateEmail = false, desired_plan = "free" } = {}) => {
    try {
      const response = await api_users.users.appleLogin({
        id_token: idToken,
        desired_plan,
        user,
        email,
        is_private_email: isPrivateEmail,
      });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const registerWithFacebook = async (facebookCode) => {
    try {
      const response = await api_users.users.facebookCodeLogin({ code: facebookCode });
      persistSessionTokens(response?.data);
      await hydrateFromCurrentUser();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.error || err.message };
    }
  };

  const logout = async () => {
    try { await api_users.users.logout(); } catch (e) {}
    clearAuthToken();
    clearLastShoppingEntry();
    localStorage.removeItem("incooking_beta_banner_closed");
    setAuthState({
      user: null,
      role: null,
      loading: false,
      krogerConnected: false,
      krogerLocationId: null,
    });
  };

  const hasCompletedOnboarding = useCallback((userId) => {
    if (!userId) return false;
    return localStorage.getItem(onboardingDoneKey(userId)) === "1";
  }, []);

  const markOnboardingCompleted = useCallback((userId) => {
    if (!userId) return;
    localStorage.setItem(onboardingDoneKey(userId), "1");
    localStorage.removeItem(onboardingForceOpenKey(userId));
  }, []);

  const requestOnboardingRerun = useCallback((userId) => {
    if (!userId) return;
    localStorage.setItem(onboardingForceOpenKey(userId), "1");
    localStorage.removeItem(onboardingDoneKey(userId));
  }, []);

  const consumeOnboardingForceOpen = useCallback((userId) => {
    if (!userId) return false;
    const key = onboardingForceOpenKey(userId);
    const forced = localStorage.getItem(key) === "1";
    if (forced) {
      localStorage.removeItem(key);
    }
    return forced;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        loginWithGoogle,
        loginWithApple,
        loginWithFacebook,
        loginWithFacebookCode,
        register,
        registerWithGoogle,
        registerWithApple,
        registerWithFacebook,
        logout,
        hydrateFromCurrentUser,
        hasCompletedOnboarding,
        markOnboardingCompleted,
        requestOnboardingRerun,
        consumeOnboardingForceOpen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
